// utils/formatPhone.js
//
// Display-only phone formatting shared by Enquire.js (Web Enquiries table)
// and Dashboard.js (Today's Enquiries table) — extracted here so both pages
// stay in sync. Previously this lived only inside Enquire.js, which is
// exactly why the Dashboard's "Today's Enquiries" table was still showing
// the raw country-code-prefixed number for WhatsApp rows while Web
// Enquiries had it stripped: Dashboard had no access to this function and
// just rendered `phone_number` as-is.
//
// IMPORTANT: this is display-only. Never apply this to a value that gets
// sent back to an API (WhatsApp replies, etc.) — those still need the full
// number with calling code. Only what's rendered in a table cell should
// ever pass through this.
//
// CHANGE: now takes a 4th argument, conversationId, and only strips the
// calling code when it's present (truthy). `source === "WhatsApp"` alone is
// NOT enough — staff can manually add an enquiry via the "Add Enquiry" form
// and pick "WhatsApp" from the Source dropdown, same as any other source
// (see saveEnquire in enquireApi.js). Those manually-typed numbers are
// stored exactly as staff entered them, with no calling code baked in, so
// stripping one off was cutting real digits off the front of the number.
// `conversation_id` is only ever set by the automated WhatsApp flows
// (saveWhatsappEnquireStub / saveWhatsappEnquire in enquireApi.js) — a
// manually-added row's conversation_id is always NULL — so it's the
// reliable signal for "this number actually came through the WhatsApp
// mirror service and does have a calling code prefix to strip."

// Same calling-code list as getCountryFromPhone() in enquireApi.js
// (backend), kept in sync so "country" here always matches what the
// backend derived.
export const COUNTRY_CALLING_CODES = {
  singapore: "65",
  myanmar: "95",
  malaysia: "60",
  thailand: "66",
  indonesia: "62",
  philippines: "63",
  vietnam: "84",
  india: "91",
  china: "86",
  japan: "81",
  "south korea": "82",
  "united kingdom": "44",
  australia: "61",
  "united states / canada": "1",
};

// All known codes, longest first, used as a fallback when `country` is
// missing/"Unknown" or doesn't match the map above (e.g. a manually typed
// enquiry) — same longest-prefix-match logic as the backend function.
const ALL_CALLING_CODES = Object.values(COUNTRY_CALLING_CODES).sort(
  (a, b) => b.length - a.length
);

// CHANGE: also strips the calling code for Source = Facebook and
// Source = Instagram rows, not just Source = WhatsApp. Both a Facebook- and
// an Instagram-referral WhatsApp conversation (ad or post click) are
// mirrored through the exact same saveWhatsappEnquireStub/saveWhatsappEnquire
// flow as a direct WhatsApp conversation — the only difference is the
// `source` value recorded on the enquiry — so both carry the same
// calling-code-prefixed phone_number and need the same display stripping.
// This does NOT touch the conversationId guard below: a manually-added
// enquiry with Source = Facebook/Instagram (added via "Add Enquiry", not
// through the WhatsApp mirror) still has conversation_id = NULL and is
// still left exactly as staff typed it, same as it always was for
// Source = WhatsApp.
const WHATSAPP_MIRROR_SOURCES = ["whatsapp", "facebook", "instagram"];

export function formatPhoneForDisplay(phone, country, source, conversationId) {
  if (!phone) return "-";

  // Only strip the calling code for rows that actually came from a real
  // WhatsApp conversation (see the CHANGE note above) — a manually-added
  // enquiry with Source = WhatsApp/Facebook but no linked conversation is
  // left exactly as staff typed it, same as any other manually-entered
  // source.
  if (!WHATSAPP_MIRROR_SOURCES.includes(String(source || "").trim().toLowerCase()) || !conversationId) {
    return phone;
  }

  const clean = String(phone).replace(/[^\d]/g, "");

  const mappedCode = COUNTRY_CALLING_CODES[String(country || "").trim().toLowerCase()];
  if (mappedCode && clean.startsWith(mappedCode) && clean.length > mappedCode.length) {
    return clean.slice(mappedCode.length);
  }

  const fallbackCode = ALL_CALLING_CODES.find(
    (c) => clean.startsWith(c) && clean.length > c.length
  );
  if (fallbackCode) return clean.slice(fallbackCode.length);

  return phone; // unrecognized code — show as-is rather than guess wrong
}